import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import BzDropdownButton, { DropdownButtonItem } from 'components/buttons/BzDropdownButton';
import Button from 'react-bootstrap/Button';
import { BodyMatcherItem, MatcherPathItem, XmlNamespaceItem } from 'components/transactionEditor/BodyMatchersList';
import {
  allMatcherOperators,
  bodyMatchersDropdownOperators,
  transactionMatchersDropdownOperators,
  wizardMatchersDropdownOperators,
} from 'views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import { getKeyByValue } from 'utils/miscUtil';
import { useTranslation } from 'react-i18next';
import XmlNamespaceList from 'components/transactionEditor/Wizards/XmlNamespaceList';
import RequestNodeWidget, { RequestNodeWidgetType } from './RequestNodeWidget';
import BzmIcon from '../icons/BzmIcon';
import WsdlDropzone from './WsdlDropzone/WsdlDropzone';
import CheckBox from 'components/ui/checkBox/CheckBox';
import _ from 'lodash';
import { Matcher } from '../../types/rest/dslTypes';
import i18n from 'i18next';
import { createAiAssistantIcon } from '../../views/AssetCatalog/AiAssistant/aiAssistantUtil';
import AiAssistantModal from '../../views/AssetCatalog/AiAssistant/AiAssistantModal';
import { getAccountAiConsent } from '../../utils/bzmUserConfigUtil';
import { clearConversationId } from '../../store/aiConversations/aiConversationsActions';
import './Wizards/ResponseNodeWidget.css';

interface BodyMatcherProps {
  id: string;
  matcherItem: BodyMatcherItem;
  onChange: (name: string, value: any, id: string) => void;
  onChangeMultipleValues?: (
    matcher: MatcherPathItem[],
    type: RequestNodeWidgetType,
    namespaces?: XmlNamespaceItem[],
    sourceId?: string,
    sampleBody?: string,
  ) => void;
  onChangeSampleBody?: (type: RequestNodeWidgetType, sourceId: string, sampleBody: string) => void;
  onXmlNamespaceChange: (list: XmlNamespaceItem[]) => void;
  onDelete: () => void;
  onAdd: () => void;
  displayAdd: boolean;
  bodyMatcherList: BodyMatcherItem[];
  isReadOnly?: boolean;
  sampleBody?: string;
  showNodeWidget?: boolean;
  isWizardModal?: boolean;
  enableAiAssistant?: boolean; // Flag to enable AI assistant functionality
  transactionId?: number;
}

const BodyMatcher = (props: BodyMatcherProps) => {
  const { t } = useTranslation('transactions');
  const dispatch = useDispatch();
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiAssistantCallerId, setAiAssistantCallerId] = useState<string>('');
  const [aiSelectionRange, setAiSelectionRange] = useState<{ start: number; end: number } | undefined>(undefined);

  // Re-apply / clear textarea selection while the AI modal is open
  React.useEffect(() => {
    const el = document.getElementById(aiAssistantCallerId) as HTMLTextAreaElement | null;
    if (!el) return;
    if (showAiAssistant && aiSelectionRange && aiSelectionRange.start !== aiSelectionRange.end) {
      el.focus();
      el.setSelectionRange(aiSelectionRange.start, aiSelectionRange.end);
    } else if (!showAiAssistant) {
      el.setSelectionRange(0, 0);
    }
  }, [showAiAssistant]);

  const resolvedTxMatchersDropdownOperators =
    props.isWizardModal === true ? wizardMatchersDropdownOperators : transactionMatchersDropdownOperators;
  const resolvedTxBodyMatchersDropdownOperators =
    props.isWizardModal === true
      ? wizardMatchersDropdownOperators
      : { ...transactionMatchersDropdownOperators, ...bodyMatchersDropdownOperators };

  const dropdownOperatorItems: DropdownButtonItem[] = [];
  Object.keys(resolvedTxBodyMatchersDropdownOperators).forEach(function(key) {
    dropdownOperatorItems.push({
      key: key,
      title: resolvedTxBodyMatchersDropdownOperators[key],
    });
  });

  const defaultOperator: DropdownButtonItem = dropdownOperatorItems.find(
    dOp => 'equals' === dOp.key,
  ) as DropdownButtonItem;

  const operatorToSelect: DropdownButtonItem =
    props.matcherItem && props.matcherItem.matcher
      ? {
          key: props.matcherItem.matcher,
          title: allMatcherOperators[props.matcherItem.matcher],
        }
      : defaultOperator;

  const handleChange = (e: any, id: string) => {
    const target = e.target as HTMLInputElement;
    props.onChange(target.name, target.value, id);
  };

  const updateXPathContent = (
    matcher: MatcherPathItem[],
    type: RequestNodeWidgetType,
    namespaces?: XmlNamespaceItem[],
    sampleBody?: string,
  ) => {
    props.onChangeMultipleValues(matcher, type, namespaces, props.id, sampleBody);
  };

  const handleXmlNamespaceChange = (list: XmlNamespaceItem[]) => {
    props.onXmlNamespaceChange(list);
    props.onChange('namespaces', list, props.id);
  };

  const getCDataXPathLabel = (matcherItem: BodyMatcherItem) => {
    if (!matcherItem || !matcherItem.cdataXpath || matcherItem.cdataXpath === '') {
      return 'No CData XPath';
    }

    return matcherItem.cdataXpath;
  };

  const getBodyContextText = (type: string) => {
    let contextLabel;
    switch (type) {
      case 'xpath':
        contextLabel = t('editor.xpathBodyMatcherContent');
        break;
      case 'cdata_xpath':
        contextLabel = t('editor.cdataXpathBodyMatcherContent');
        break;
      case 'json':
        contextLabel = t('editor.jsonBodyMatcherContent');
        break;
      case 'wsdl':
        contextLabel = t('editor.wsdlBodyMatcherContent');
        break;
      default:
        contextLabel = t('editor.bodyMatcherContent');
    }
    return `${contextLabel}`;
  };

  const getType = operatorToSelect => {
    switch (operatorToSelect.key) {
      case Matcher.matches_xml:
        return 'xpath';
      case Matcher.matches_xml_cdata:
        return 'cdata_xpath';
      case Matcher.matches_json:
        return 'json';
      case Matcher.matches_xml_schema:
        return 'wsdl';
      case Matcher.matches_graphql_body:
        return null;
      default:
        return null;
    }
  };

  const isAbsent = getKeyByValue(resolvedTxMatchersDropdownOperators, props.matcherItem.matcher) === 'absent';
  const type: RequestNodeWidgetType = getType(operatorToSelect);
  const bodyContentText = getBodyContextText(type);

  // AI Assistant functionality — only for expression operators that require authoring a pattern
  const aiAllowedOperators = new Set([
    Matcher.matches,
    Matcher.not_matches,
    Matcher.matches_xml,
    Matcher.matches_xml_cdata,
    Matcher.matches_json,
  ]);
  const shouldShowAiIcon =
    props.enableAiAssistant &&
    aiAllowedOperators.has(props.matcherItem.matcher as Matcher) &&
    !props.isReadOnly &&
    getAccountAiConsent();

  const stableNewTxSuffix = React.useRef(`new-${Date.now()}`).current;
  const bodyContentFieldId =
    props.transactionId && props.transactionId !== -1
      ? `qa-body-content-${props.transactionId}-${props.id}`
      : `qa-body-content-${stableNewTxSuffix}-${props.id}`;

  const captureBodySelection = (callerId: string) => {
    const el = document.getElementById(callerId) as HTMLTextAreaElement | null;
    if (el) {
      setAiSelectionRange({ start: el.selectionStart || 0, end: el.selectionEnd || 0 });
    } else {
      setAiSelectionRange(undefined);
    }
  };

  const handleAiAssistantShow = (callerId: string) => {
    setAiAssistantCallerId(callerId);
    setShowAiAssistant(true);
  };

  const handleAiAssistantUpdate = (value: string) => {
    props.onChange('content', value, props.id);
  };

  const handleDelete = () => {
    // Clear the AI conversation for this field ID
    if (props.enableAiAssistant) {
      dispatch(clearConversationId(bodyContentFieldId));
    }
    // Call the original delete handler
    props.onDelete();
  };

  const currentValue = isAbsent ? '' : props.matcherItem.content ? props.matcherItem.content : '';

  // Persists just the wizard's sample body text (not any matcher content) onto this row's
  // family — used when the wizard closes without an explicit "Select" so the sample body isn't
  // lost, without touching whatever matchers are already saved.
  const updateSampleBody = (sampleBody: string) => {
    props.onChangeSampleBody?.(type, props.id, sampleBody);
  };

  const widgetObject = {
    id: props.id,
    isReadOnly: props.isReadOnly,
    onAdd: props.onAdd,
    sampleBody: props.sampleBody as string,
    type: type,
    xmlNamespaceList: props.matcherItem.namespaces,
    updateXPathContent: updateXPathContent,
    updateSampleBody: updateSampleBody,
    bodyMatcherList: props.bodyMatcherList,
  };

  return (
    <Row>
      <Col md={5} className={'ml-4 pl-5 pr-5 elevation1 bg-white'}>
        <div id={props.id} className="mb-5 mt-6">
          <label className={'font-size-lg font-weight-semi-bold'} htmlFor={`qa-toggle-${props.id}`}>
            {t('editor.bodyMatcherLabel')}
          </label>
          <BzDropdownButton
            disabled={props.isReadOnly}
            id={props.id}
            dropdownItems={dropdownOperatorItems}
            selectedItem={operatorToSelect}
            onSelectedItem={(item: DropdownButtonItem) => props.onChange('matcher', item.key, props.id)}
            variant="secondary"
            maxHeight={'300px'}
          />
        </div>
        <div className="mb-5">
          <Form.Group controlId={'qa-textarea-body-matcher-' + props.id} className={'w-auto'}>
            <div className="d-flex align-items-center">
              <label className={'font-size-lg font-weight-semi-bold'}>{bodyContentText}</label>
            </div>

            {type === 'wsdl' ? (
              <WsdlDropzone
                onChange={val => props.onChange('content', val, props.id)}
                value={isAbsent ? '' : props.matcherItem.content ? props.matcherItem.content : ''}
              />
            ) : (
              <>
                <div className="d-flex align-items-center">
                  {shouldShowAiIcon && (
                    <Button
                      variant={'link'}
                      className="btn btn-link btn-no-hover mb-1 mt-0 pb-2 pt-0 pl-0"
                      onMouseDown={() => captureBodySelection(bodyContentFieldId)}
                      onClick={() => handleAiAssistantShow(bodyContentFieldId)}
                    >
                      {createAiAssistantIcon()}
                      <span title={'Ai Assistant'} className={'ml-3 font-size-base font-weight-semi-bold text-primary'}>
                        Ai Assistant
                      </span>
                    </Button>
                  )}
                  {(type === 'xpath' || type === 'cdata_xpath' || type === 'json') && (
                    <>
                      <div className="vdivider-response-wizard ml-2 mr-2"></div>
                      <RequestNodeWidget {...widgetObject} />
                    </>
                  )}
                </div>
                <Form.Control
                  id={bodyContentFieldId}
                  autoComplete={'off'}
                  as="textarea"
                  rows={5}
                  name="content"
                  onChange={(event: any) => handleChange(event, props.id)}
                  placeholder={isAbsent ? '' : 'Enter ' + bodyContentText + '...'}
                  value={isAbsent ? '' : props.matcherItem.content ? props.matcherItem.content : ''}
                />
                {type === 'cdata_xpath' && [
                  <div key={'cdata-xpath-label'} className={'mt-5'}>
                    <div>
                      <label className={'font-size-lg font-weight-semi-bold'}>
                        {i18n.t('transactions:editor.cdataXpath')}
                      </label>
                    </div>
                    <div>
                      <span>{getCDataXPathLabel(props.matcherItem)}</span>
                    </div>
                  </div>,
                ]}
              </>
            )}
          </Form.Group>
        </div>
        {/*Show only for xpath or xml path matchers */}
        {(type === 'xpath' || type === 'cdata_xpath' || type === 'json') && (
          <div className="mb-5">
            <CheckBox
              label={t('editor.required')}
              title={t('editor.requiredHint')}
              onCheck={() => {
                props.onChange('isRequired', !props.matcherItem.isRequired, props.id);
              }}
              checked={!_.isNil(props.matcherItem.isRequired) ? props.matcherItem.isRequired : true}
            />
          </div>
        )}
        <div className={'mb-5 pull-right'}>
          <Button
            disabled={props.isReadOnly}
            id={'qa-btn-delete-body-matcher-' + props.id}
            onClick={handleDelete}
            variant="danger"
            className="btn btn-danger btn-circular"
            title={t('editor.deleteBodyMatcher')}
          >
            <BzmIcon variant={'icon-bzm-delete'} />
          </Button>
          {props.displayAdd && (
            <Button
              disabled={props.isReadOnly}
              id={'qa-btn-add-body-matcher'}
              onClick={props.onAdd}
              variant="secondary"
              className="btn-circular ml-4"
              title={t('editor.addNewBodyMatcher')}
            >
              <BzmIcon variant={'icon-bzm-add'} className={'icon-theme-blue'} />
            </Button>
          )}
        </div>
      </Col>
      <Col md={6} className={'ml-8'}>
        {(operatorToSelect.key === Matcher.matches_xml || operatorToSelect.key === Matcher.matches_xml_cdata) && (
          <XmlNamespaceList
            matcherType={operatorToSelect.key}
            isReadOnly={props.isReadOnly}
            id={'id-xml-namespace'}
            xmlNamespaceList={props.matcherItem.namespaces}
            onXmlNamespaceChange={handleXmlNamespaceChange}
          />
        )}
      </Col>
      {showAiAssistant && (
        <AiAssistantModal
          callerId={aiAssistantCallerId}
          updateFunction={handleAiAssistantUpdate}
          currentValue={currentValue}
          onClose={() => setShowAiAssistant(false)}
          title="Request Matcher Body"
          matcherType={props.matcherItem.matcher as string}
          selectionRange={aiSelectionRange}
        />
      )}
    </Row>
  );
};

export default BodyMatcher;
